import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./PaymentsAdminModal.module.css";

const EMPTY_PLAN_FORM = { name: "", amount: "", interval: "monthly", audience: "aluno", is_active: true, unlocks_student_billing: true };
const INTERVAL_LABELS = {
  weekly: "Semanal", biweekly: "Quinzenal", monthly: "Mensal", bimonthly: "Bimestral",
  quarterly: "Trimestral", semiannual: "Semestral", yearly: "Anual",
};
const INTERVAL_OPTIONS = ["weekly", "biweekly", "monthly", "bimonthly", "quarterly", "semiannual", "yearly"];

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(" - ");
  return JSON.stringify(detail);
}

function formatMoney(value) {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * Uma seção de planos (tabela + form de criação colapsável). Usado 2x pelo
 * admin (Alunos / Professores, cada um com sua audiência fixa) e 1x pelo
 * professor (sem título de seção nem coluna de tier — audience é sempre
 * "aluno", forçado no backend).
 */
function PlanGroup({
  title, subtitle, plans, showTierColumn, showSubscribersColumn,
  editingPlanId, editPlanForm, setEditPlanForm, savingPlan,
  onStartEdit, onCancelEdit, onSavePlan, onToggleActive,
  newPlanOpen, onOpenNewPlan, onCloseNewPlan,
  newPlan, setNewPlan, creatingPlan, onCreatePlan,
}) {
  const colSpan = 3 + (showTierColumn ? 1 : 0) + (showSubscribersColumn ? 1 : 0) + 2;

  return (
    <div className={styles.planGroup}>
      {title && (
        <div className={styles.planGroupHeader}>
          <div>
            <h4 className={styles.planGroupTitle}>{title}</h4>
            {subtitle && <p className={styles.planGroupSub}>{subtitle}</p>}
          </div>
          {!newPlanOpen && (
            <button className={styles.btnPrimarySmall} onClick={onOpenNewPlan}>
              <FontAwesomeIcon icon={faPlus} /> Novo plano
            </button>
          )}
        </div>
      )}

      {newPlanOpen && (
        <div className={styles.newPlanCard}>
          <div className={styles.newPlanCardGrid}>
            <div>
              <label className={styles.fieldLabel}>Nome do plano</label>
              <input className={styles.inlineInput} style={{ width: "100%" }} placeholder="Ex: Mensal" value={newPlan.name}
                onChange={e => setNewPlan(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className={styles.fieldLabel}>Valor</label>
              <input className={styles.inlineInput} style={{ width: "100%" }} placeholder="0,00" type="number" step="0.01" value={newPlan.amount}
                onChange={e => setNewPlan(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <label className={styles.fieldLabel}>Intervalo</label>
              <select className={styles.inlineInput} style={{ width: "100%" }} value={newPlan.interval}
                onChange={e => setNewPlan(f => ({ ...f, interval: e.target.value }))}>
                {INTERVAL_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{INTERVAL_LABELS[opt]}</option>
                ))}
              </select>
            </div>
          </div>

          {showTierColumn && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "var(--text-0-70)", marginTop: "14px" }}>
              <input type="checkbox" checked={newPlan.unlocks_student_billing}
                onChange={e => setNewPlan(f => ({ ...f, unlocks_student_billing: e.target.checked }))}
                style={{ marginTop: "2px" }} />
              <span>
                Libera gestão de pagamentos dos alunos
                <br />
                <span className={styles.muted} style={{ fontSize: "var(--fs-xs)" }}>
                  Plano Premium: professor pode cobrar mensalidade dos próprios alunos. Desmarcado = plano Básico, só gestão de treinos.
                </span>
              </span>
            </label>
          )}

          <div className={styles.newPlanCardActions}>
            <button className={styles.btnSecondarySmall} onClick={onCloseNewPlan}>Cancelar</button>
            <button className={styles.btnPrimarySmall} onClick={onCreatePlan} disabled={creatingPlan}>
              {creatingPlan ? "Criando…" : "Criar plano"}
            </button>
          </div>
        </div>
      )}

      <table className={styles.table} role="table">
        <thead>
          <tr role="row">
            <th>Nome</th><th>Valor</th><th>Intervalo</th>
            {showTierColumn && <th>Gestão pagto. alunos</th>}
            {showSubscribersColumn && <th>Assinantes</th>}
            <th>Ativo</th><th></th>
          </tr>
        </thead>
        <tbody>
          {plans.map(plan => (
            editingPlanId === plan.id ? (
              <tr key={plan.id} role="row" className={styles.editingRow}>
                <td role="cell" data-label="Nome"><input className={styles.inlineInput} aria-label="Nome do plano" value={editPlanForm.name}
                  onChange={e => setEditPlanForm(f => ({ ...f, name: e.target.value }))} /></td>
                <td role="cell" data-label="Valor"><input className={styles.inlineInput} aria-label="Valor do plano" type="number" step="0.01" value={editPlanForm.amount}
                  onChange={e => setEditPlanForm(f => ({ ...f, amount: e.target.value }))} /></td>
                <td role="cell" data-label="Intervalo">
                  <select className={styles.inlineInput} aria-label="Intervalo de cobrança" value={editPlanForm.interval}
                    onChange={e => setEditPlanForm(f => ({ ...f, interval: e.target.value }))}>
                    {INTERVAL_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{INTERVAL_LABELS[opt]}</option>
                    ))}
                  </select>
                </td>
                {showTierColumn && (
                  <td role="cell" data-label="Gestão pagto. alunos">
                    <input type="checkbox" checked={editPlanForm.unlocks_student_billing}
                      title="Libera a gestão de pagamentos dos próprios alunos pro professor"
                      aria-label="Libera a gestão de pagamentos dos próprios alunos"
                      onChange={e => setEditPlanForm(f => ({ ...f, unlocks_student_billing: e.target.checked }))} />
                  </td>
                )}
                {showSubscribersColumn && <td role="cell" data-label="Assinantes" className={styles.muted}>{plan.active_subscribers ?? 0}</td>}
                <td role="cell" data-label="Ativo">
                  <input type="checkbox" checked={editPlanForm.is_active}
                    aria-label="Plano ativo"
                    onChange={e => setEditPlanForm(f => ({ ...f, is_active: e.target.checked }))} />
                </td>
                <td role="cell" className={styles.actionsCell}>
                  <button className={styles.iconBtn} onClick={() => onSavePlan(plan.id)} disabled={savingPlan} title="Salvar" aria-label="Salvar plano">
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button className={styles.iconBtn} onClick={onCancelEdit} title="Cancelar" aria-label="Cancelar edição">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={plan.id} role="row">
                <td role="cell" data-label="Nome" className={styles.cellName}>{plan.name}</td>
                <td role="cell" data-label="Valor">{formatMoney(plan.amount)}</td>
                <td role="cell" data-label="Intervalo">{INTERVAL_LABELS[plan.interval] || plan.interval}</td>
                {showTierColumn && (
                  <td role="cell" data-label="Gestão pagto. alunos">{plan.unlocks_student_billing ? "Sim" : "Não"}</td>
                )}
                {showSubscribersColumn && <td role="cell" data-label="Assinantes">{plan.active_subscribers ?? 0}</td>}
                <td role="cell" data-label="Ativo">
                  <button
                    className={`${styles.statusPill} ${plan.is_active ? styles.statusPillActive : styles.statusPillInactive} touchTarget`}
                    onClick={() => onToggleActive(plan)}
                    title="Clique para alternar"
                    aria-label={`Plano ${plan.name}: ${plan.is_active ? "ativo" : "inativo"}. Clique para alternar.`}
                  >
                    {plan.is_active ? "Ativo" : "Inativo"}
                  </button>
                </td>
                <td role="cell" className={styles.actionsCell}>
                  <button className={styles.iconBtn} onClick={() => onStartEdit(plan)} title="Editar" aria-label="Editar plano">
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                </td>
              </tr>
            )
          ))}
          {plans.length === 0 && (
            <tr role="row"><td role="cell" colSpan={colSpan} className={styles.emptyMsg}>Nenhum plano cadastrado.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * CRUD de planos — extraída de PaymentsAdminModal.jsx pra ser reusada também
 * pelo ProfessorBillingModal.jsx (professor gerenciando o próprio plano de
 * mensalidade), evitando duplicar a mesma lógica de formulário/validação.
 *
 * @param {string} listUrl - GET pra listar planos
 * @param {string} createUrl - POST pra criar
 * @param {string} updateUrlPrefix - PUT em `${updateUrlPrefix}/${planId}`
 * @param {boolean} showAudienceField - true (admin): separa em duas seções
 *   fixas (Alunos / Professores), com coluna de tier e assinantes na seção
 *   de professor. false (professor gerenciando o próprio plano): lista única
 *   sem título de seção, sem tier — audience já é sempre "aluno" no backend.
 */
export default function PlanEditorTable({ listUrl, createUrl, updateUrlPrefix, showAudienceField = true }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newPlanOpenFor, setNewPlanOpenFor] = useState(null); // null | "aluno" | "professor" | "single"
  const [newPlan, setNewPlan] = useState(EMPTY_PLAN_FORM);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editPlanForm, setEditPlanForm] = useState(EMPTY_PLAN_FORM);
  const [savingPlan, setSavingPlan] = useState(false);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(listUrl);
      setPlans(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  function openNewPlan(audience) {
    setNewPlan({ ...EMPTY_PLAN_FORM, audience });
    setNewPlanOpenFor(audience);
  }

  function closeNewPlan() {
    setNewPlanOpenFor(null);
    setNewPlan(EMPTY_PLAN_FORM);
  }

  async function handleCreatePlan() {
    if (!newPlan.name.trim() || !newPlan.amount) {
      setError("Preencha nome e valor do plano.");
      return;
    }
    setCreatingPlan(true);
    setError("");
    try {
      const payload = { name: newPlan.name.trim(), amount: Number(newPlan.amount), interval: newPlan.interval, is_active: newPlan.is_active };
      if (showAudienceField) {
        payload.audience = newPlan.audience;
        if (newPlan.audience === "professor") payload.unlocks_student_billing = newPlan.unlocks_student_billing;
      }
      await api.post(createUrl, payload);
      closeNewPlan();
      await loadPlans();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setCreatingPlan(false);
    }
  }

  function startEditPlan(plan) {
    setEditingPlanId(plan.id);
    setEditPlanForm({
      name: plan.name, amount: String(plan.amount), interval: plan.interval,
      audience: plan.audience || "aluno", is_active: plan.is_active,
      unlocks_student_billing: plan.unlocks_student_billing ?? true,
    });
  }

  async function handleSavePlan(planId) {
    setSavingPlan(true);
    setError("");
    try {
      const payload = { name: editPlanForm.name.trim(), amount: Number(editPlanForm.amount), interval: editPlanForm.interval, is_active: editPlanForm.is_active };
      if (showAudienceField) {
        payload.audience = editPlanForm.audience;
        if (editPlanForm.audience === "professor") payload.unlocks_student_billing = editPlanForm.unlocks_student_billing;
      }
      await api.put(`${updateUrlPrefix}/${planId}`, payload);
      setEditingPlanId(null);
      await loadPlans();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSavingPlan(false);
    }
  }

  async function handleTogglePlanActive(plan) {
    setError("");
    try {
      await api.put(`${updateUrlPrefix}/${plan.id}`, { is_active: !plan.is_active });
      await loadPlans();
    } catch (err) {
      setError(extractError(err));
    }
  }

  if (loading) {
    return (
      <div className={styles.plansSection}>
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.loadingMsg}>Carregando…</div>
      </div>
    );
  }

  const sharedProps = {
    editingPlanId, editPlanForm, setEditPlanForm, savingPlan,
    onStartEdit: startEditPlan, onCancelEdit: () => setEditingPlanId(null),
    onSavePlan: handleSavePlan, onToggleActive: handleTogglePlanActive,
    newPlan, setNewPlan, creatingPlan, onCreatePlan: handleCreatePlan,
  };

  return (
    <div className={styles.plansSection}>
      {error && <div className={styles.error}>{error}</div>}

      {showAudienceField ? (
        <>
          <PlanGroup
            {...sharedProps}
            title="Planos para Alunos"
            subtitle="Self-service - aluno sem professor paga direto pela plataforma."
            plans={plans.filter(p => p.audience === "aluno")}
            showTierColumn={false}
            showSubscribersColumn
            newPlanOpen={newPlanOpenFor === "aluno"}
            onOpenNewPlan={() => openNewPlan("aluno")}
            onCloseNewPlan={closeNewPlan}
          />
          <PlanGroup
            {...sharedProps}
            title="Planos para Professores"
            subtitle="Professor assina pra usar a plataforma - Premium também libera cobrar mensalidade dos próprios alunos."
            plans={plans.filter(p => p.audience === "professor")}
            showTierColumn
            showSubscribersColumn
            newPlanOpen={newPlanOpenFor === "professor"}
            onOpenNewPlan={() => openNewPlan("professor")}
            onCloseNewPlan={closeNewPlan}
          />
        </>
      ) : (
        <>
          {!newPlanOpenFor && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
              <button className={styles.btnPrimarySmall} onClick={() => openNewPlan("single")}>
                <FontAwesomeIcon icon={faPlus} /> Novo plano
              </button>
            </div>
          )}
          <PlanGroup
            {...sharedProps}
            plans={plans}
            showTierColumn={false}
            showSubscribersColumn={false}
            newPlanOpen={newPlanOpenFor === "single"}
            onOpenNewPlan={() => openNewPlan("single")}
            onCloseNewPlan={closeNewPlan}
          />
        </>
      )}
    </div>
  );
}
